/**
 * Generic tabbed activity region for index-page detail panels: pass a set of
 * tabs (label + count + render); zero-count tabs are hidden and the whole
 * section disappears when everything is empty. See docs/expandable-index-pages.md.
 */
import { useState } from 'react';
import { Datagrid, ListContextProvider, useList } from 'react-admin';
import { Box, Tab, Tabs } from '@mui/material';

import { SectionLabel } from './primitives';

export interface ActivityTab {
  key: string;
  label: string;
  count: number;
  render: () => React.ReactNode;
}

export const ActivityTabs = ({
  tabs,
  title = 'Activity',
}: {
  tabs: ActivityTab[];
  title?: string;
}) => {
  const visible = tabs.filter(tab => tab.count > 0);
  const [active, setActive] = useState(0);
  if (!visible.length) return null;
  const activeIndex = Math.min(active, visible.length - 1);

  return (
    <Box>
      <SectionLabel>{title}</SectionLabel>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
        <Tabs
          value={activeIndex}
          onChange={(_event, value: number) => setActive(value)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 36,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 36, fontSize: 12.5, px: 1.75 },
          }}
        >
          {visible.map(tab => (
            <Tab key={tab.key} label={`${tab.label} (${tab.count})`} />
          ))}
        </Tabs>
        <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
          {visible[activeIndex].render()}
        </Box>
      </Box>
    </Box>
  );
};

// `id`/`uuid` are nullable, not merely optional: the generated admin types make almost every
// scalar `Maybe<T>`, so a row arriving as `{id: null}` is ordinary rather than exceptional.
// `withIds` already coalesces it below — this only stops the type from forcing callers to assert
// otherwise.
interface ActivityRow { id?: string | number | null, uuid?: string | null }

/**
 * Ensure every embedded row has an `id` so useList can key/sort it.
 *
 * The synthetic fallback is prefixed rather than a bare index, because a bare one collides with
 * real ids: `[{id: 1}, {}, {}]` gives the second row index 1, duplicating the first. React then
 * warns and may drop or duplicate rows. Prefixing keeps the two id spaces disjoint.
 */
const withIds = <T extends ActivityRow>(rows: readonly T[]) =>
  rows.map((row, index) => ({ ...row, id: row.id ?? row.uuid ?? `row-${index}` }));

/**
 * A react-admin <Datagrid> fed by a local array via useList/ListContextProvider —
 * the framework renders the rows and empty state. `rows` is the embedded array
 * (state transitions, packages, jobs, …); `children` are the column fields
 * (<TextField>/<DateField>/<FunctionField>).
 *
 * `nowrapColumns` names the `source`s that should stay on one line — timestamps,
 * typically. Everything else wraps, which is deliberate: long free-text cells
 * (notes, reasons, JSON details) must not widen the table, because their
 * min-content width propagates through the outer list table and gives the whole
 * page a horizontal scrollbar.
 */
export const ActivityGrid = <T extends ActivityRow>({
  rows,
  nowrapColumns = [],
  children,
}: {
  rows: readonly T[];
  nowrapColumns?: string[];
  children: React.ReactNode;
}) => {
  const listContext = useList({ data: withIds(rows) });
  return (
    <ListContextProvider value={listContext}>
      <Datagrid
        bulkActionButtons={false}
        rowClick={false}
        sx={{
          '& .RaDatagrid-rowCell': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            verticalAlign: 'top',
          },
          ...(nowrapColumns.length ? {
            [nowrapColumns.map(source => `& .column-${source}`).join(', ')]: {
              whiteSpace: 'nowrap',
            },
          } : {}),
        }}
      >
        {children}
      </Datagrid>
    </ListContextProvider>
  );
};

export default ActivityTabs;
